import { useState, useEffect } from 'react';
import axiosClient from '../api/axiosClient';
import i18n from '../i18n/i18n';

interface User {
  id?: number;
  username: string;
  email?: string;
  role?: 'user' | 'admin';
  preferredLanguage?: 'en' | 'es' | 'de' | 'fr';
}

const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // 1) Captura token desde querystring (?token=...) SOLO cuando está embebido en iframe
    try {
      let isEmbedded = false;
      try {
        isEmbedded = window.self !== window.top;
      } catch {
        // Si hay restricción de acceso por cross-origin, asumimos que está embebido
        isEmbedded = true;
      }

      if (isEmbedded) {
        const url = new URL(window.location.href);
        const tokenFromUrl = url.searchParams.get('token');
        if (tokenFromUrl) {
          localStorage.setItem('accessToken', tokenFromUrl);
          url.searchParams.delete('token');
          window.history.replaceState({}, document.title, url.toString());
        }
      }
    } catch {}

    // 2) Verifica si hay un token en localStorage y pobla el usuario
    const token = localStorage.getItem('accessToken');
    const storedUser = localStorage.getItem('user');
    const init = async () => {
      if (token) {
        if (storedUser) {
          try {
            const parsed = JSON.parse(storedUser) as User;
            setUser(parsed);
            // Solo cambiar idioma si no hay uno seleccionado manualmente
            const manualLanguage = localStorage.getItem('preferredLanguage');
            if (parsed.preferredLanguage && !manualLanguage) {
              i18n.changeLanguage(parsed.preferredLanguage);
            }
          } catch {
            // Si hay token pero no usuario válido almacenado, intenta obtenerlo del backend
            try {
              const { data } = await axiosClient.get('/auth/me');
              const fetchedUser: User = { id: data.id, username: data.username, email: data.email, role: data.role, preferredLanguage: data.preferredLanguage };
              localStorage.setItem('user', JSON.stringify(fetchedUser));
              setUser(fetchedUser);
              // Solo cambiar idioma si no hay uno seleccionado manualmente
              const manualLanguage = localStorage.getItem('preferredLanguage');
              if (fetchedUser.preferredLanguage && !manualLanguage) {
                i18n.changeLanguage(fetchedUser.preferredLanguage);
              }
            } catch {
              setUser({ username: 'Usuario' });
            }
          }
        } else {
          // No hay usuario almacenado, intenta obtenerlo del backend
          try {
            const { data } = await axiosClient.get('/auth/me');
            const fetchedUser: User = { id: data.id, username: data.username, email: data.email, role: data.role, preferredLanguage: data.preferredLanguage };
            localStorage.setItem('user', JSON.stringify(fetchedUser));
            setUser(fetchedUser);
            // Solo cambiar idioma si no hay uno seleccionado manualmente
            const manualLanguage = localStorage.getItem('preferredLanguage');
            if (fetchedUser.preferredLanguage && !manualLanguage) {
              i18n.changeLanguage(fetchedUser.preferredLanguage);
            }
          } catch {
            setUser({ username: 'Usuario' });
          }
        }
      }
      setLoading(false);
    };
    init();
  }, []);

  const login = async (username: string, password: string) => {
    try {
      setLoading(true);
      const response = await axiosClient.post('/auth/login', { username, password });
      const { access_token, user: userPayload } = response.data;

      // Almacenar el token
      localStorage.setItem('accessToken', access_token);
      const newUser: User = { username: userPayload.username, role: userPayload.role, id: userPayload.id, email: userPayload.email, preferredLanguage: userPayload.preferredLanguage };
      localStorage.setItem('user', JSON.stringify(newUser));
      setUser(newUser);
      // Solo cambiar idioma si no hay uno seleccionado manualmente
      const manualLanguage = localStorage.getItem('preferredLanguage');
      if (newUser.preferredLanguage && !manualLanguage) {
        i18n.changeLanguage(newUser.preferredLanguage);
      }
      return true;
    } catch (error) {
      console.error('Login failed:', error);
      // Podrías mostrar un toast o mensaje de error
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    setUser(null);
  };

  return { user, loading, login, logout };
};

export default useAuth;